import { createPortal } from "react-dom";
import { Info, LoaderCircle, X } from "lucide-react";
import { useRef, useState } from "react";
import { apiClient, type BulkTarget, type CenaRecalcRun } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { useDismiss } from "@/shared/hooks/useDismiss";
import {
  CENA_RECALC_PART_GROUPS,
  CENA_RECALC_PART_HINTS,
  CENA_RECALC_PART_LABELS,
  CENA_RECALC_PARTS,
  type CenaRecalcPart
} from "@/shared/vocabulary/cena-recalc-parts";

/**
 * Recalcular o CENA das menções escolhidas pelo cadastro de hoje
 * (2026-11-04). Cinco caixinhas, todas desmarcadas: o que não for marcado
 * fica exatamente como está gravado. Nada de IA e nada de estimativa — o
 * servidor resolve o alvo no clique e responde 202.
 *
 * Erro (409 de recálculo em andamento, 422, 429) fica DENTRO do diálogo,
 * com o que a pessoa marcou preservado; só o sucesso o fecha.
 */
export function CenaRecalcDialog({
  token,
  count,
  targetLabel,
  target,
  onApplied,
  onClose
}: {
  token: string;
  /** `null` = "as menções do filtro" (o total ainda não se sabe). */
  count: number | null;
  targetLabel: string;
  target: () => BulkTarget;
  onApplied: (run: CenaRecalcRun) => void;
  onClose: () => void;
}) {
  const [parts, setParts] = useState<CenaRecalcPart[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O alvo não muda com o diálogo aberto; a função do pai é recriada a
  // cada render dele, então fica guardada.
  const targetRef = useRef(target);

  useDismiss(true, () => {
    if (!sending) onClose();
  });

  const toggle = (part: CenaRecalcPart) =>
    setParts(current =>
      current.includes(part) ? current.filter(item => item !== part) : [...current, part]
    );

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      // Na ordem do vocabulário, não na do clique: o corpo sai igual
      // qualquer que seja a ordem em que a pessoa marcou.
      const ordered = CENA_RECALC_PARTS.filter(part => parts.includes(part));
      onApplied(await apiClient.requestCenaRecalc(token, targetRef.current(), ordered));
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível iniciar o recálculo."));
      setSending(false);
    }
  };

  const summary =
    count === null
      ? "As menções do filtro"
      : `${count.toLocaleString("pt-BR")} ${count === 1 ? "menção selecionada" : "menções selecionadas"}`;

  return createPortal(
    <div className="modal-overlay" onClick={sending ? undefined : onClose}>
      <div
        className="modal-container bulk-dialog ai-reprocess-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cena-recalc-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">CENA</span>
            <h2 id="cena-recalc-title">Recalcular CENA</h2>
            <p className="bulk-dialog-target">
              {summary}. {targetLabel}
            </p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={sending}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <div className="ai-reprocess">
            {CENA_RECALC_PART_GROUPS.map(group => (
              <fieldset key={group.title} className="ai-reprocess-step">
                <legend className="ai-reprocess-step-title">{group.title}</legend>
                {group.parts.map(part => (
                  <label key={part} className="filter-check ai-reprocess-reviewed">
                    <input
                      type="checkbox"
                      checked={parts.includes(part)}
                      onChange={() => toggle(part)}
                      disabled={sending}
                    />
                    <span>
                      {CENA_RECALC_PART_LABELS[part]}
                      <small>{CENA_RECALC_PART_HINTS[part]}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
            ))}
            <div className="ai-reprocess-callout">
              <Info size={16} aria-hidden="true" />
              <p>
                Usa o cadastro e a configuração do CENA de hoje. Não chama IA e não muda a
                classificação. Menções descartadas ficam de fora. Amostras salvas só mudam com
                “Atualizar dados”; rascunhos abertos se atualizam sozinhos. Em TV e rádio o
                formato precisa da duração, que só existe nas matérias que entraram com ela.
              </p>
            </div>
            {error && (
              <div className="account-feedback error" role="alert" aria-live="polite">
                {error}
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button type="button" className="secondary-button" onClick={onClose} disabled={sending}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void submit()}
            disabled={parts.length === 0 || sending}
          >
            {sending ? <LoaderCircle className="spin" size={16} /> : "Recalcular"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
